import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { api } from '@/lib/api'
import { cn, formatDateTime } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import type {
  FinanceReport,
  FinanceReportAnalysis,
  FinanceReportStats,
} from '@/types'

const STATUS_STYLES: Record<string, string> = {
  running: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
}

const STATUS_LABELS: Record<string, string> = {
  running: '生成中',
  completed: '已完成',
  failed: '失败',
}

function pct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return `${(value * 100).toFixed(1)}%`
}

/** 下载率 = 被下载过的素材数 / 素材总数 */
function downloadRate(stats: Partial<FinanceReportStats>): number | null {
  const total = stats.total_variants ?? 0
  if (total <= 0) return null
  return (stats.downloaded_variants ?? 0) / total
}

export function FinanceReportPage() {
  const queryClient = useQueryClient()
  const [creativeType, setCreativeType] = useState('image')
  const [date, setDate] = useState('')

  const batchesQuery = useQuery({
    queryKey: ['finance-report-batches'],
    queryFn: () => api.getFeedbackBatches(),
  })

  const dates = useMemo(() => {
    const set = new Set<string>()
    for (const batch of batchesQuery.data ?? []) {
      if (batch.creative_type === creativeType && batch.date) set.add(batch.date)
    }
    return [...set].sort().reverse()
  }, [batchesQuery.data, creativeType])

  const selectedDate = date && dates.includes(date) ? date : dates[0] ?? ''
  const batchMarkets = useMemo(
    () =>
      (batchesQuery.data ?? [])
        .filter((b) => b.creative_type === creativeType && b.date === selectedDate)
        .map((b) => b.market)
        .filter(Boolean) as string[],
    [batchesQuery.data, creativeType, selectedDate],
  )

  // 拉全部历史版本（每次生成都会落库为新行），前端按市场分组、可切换版本
  const reportsQuery = useQuery({
    queryKey: ['finance-reports', selectedDate, creativeType],
    queryFn: () => api.getFinanceReports({ date: selectedDate, type: creativeType, latest: false }),
    enabled: Boolean(selectedDate),
    // poll while any report is still generating
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.status === 'running') ? 5000 : false,
  })

  const generateMutation = useMutation({
    mutationFn: () => api.generateFinanceReports({ date: selectedDate, creative_type: creativeType }),
    onSuccess: (result) => {
      const started = result.reports.length
      const skipped = result.skipped_markets.length
      toast({
        title: `已触发 ${started} 个市场的报告生成${skipped ? `（${skipped} 个生成中，已跳过）` : ''}`,
      })
      queryClient.invalidateQueries({ queryKey: ['finance-reports', selectedDate, creativeType] })
    },
    onError: () => {
      toast({ title: '触发报告生成失败', variant: 'destructive' })
    },
  })

  const reports = reportsQuery.data ?? []
  const generating = generateMutation.isPending || reports.some((r) => r.status === 'running')

  // 按市场分组，组内版本按 created_at 倒序（后端已排序）
  const marketGroups = useMemo(() => {
    const groups: { market: string; versions: FinanceReport[] }[] = []
    const byMarket = new Map<string, FinanceReport[]>()
    for (const report of reports) {
      let bucket = byMarket.get(report.market)
      if (!bucket) {
        bucket = []
        byMarket.set(report.market, bucket)
        groups.push({ market: report.market, versions: bucket })
      }
      bucket.push(report)
    }
    return groups
  }, [reports])

  // 每个市场当前查看的版本（默认最新）；切换日期/类型时重置
  const [pinnedVersions, setPinnedVersions] = useState<Record<string, string>>({})
  useEffect(() => {
    setPinnedVersions({})
  }, [selectedDate, creativeType])

  // 重新生成确认弹窗
  const [confirmOpen, setConfirmOpen] = useState(false)
  const latestCreatedAt = useMemo(
    () =>
      reports.reduce<string | null>(
        (acc, report) => (!acc || report.created_at > acc ? report.created_at : acc),
        null,
      ),
    [reports],
  )
  const handleGenerateClick = () => {
    if (reports.length > 0) {
      setConfirmOpen(true)
    } else {
      generateMutation.mutate()
    }
  }

  // 每个市场当前展示的版本：用户选中的 > 最新已完成的 > 最新的。
  // 重新生成时新插入的 running 行不抢占展示，旧报告保持可读。
  const displayedReports = useMemo(
    () =>
      marketGroups.map(
        (group) =>
          group.versions.find((v) => v.id === pinnedVersions[group.market]) ??
          group.versions.find((v) => v.status === 'completed') ??
          group.versions[0],
      ),
    [marketGroups, pinnedVersions],
  )

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Feedback Analysis</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          按日期统计各市场反馈素材的采纳与下载情况，并结合图片特征与人工反馈生成 AI 分析报告。
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          类型
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            value={creativeType}
            onChange={(event) => {
              setCreativeType(event.target.value)
              setDate('')
            }}
          >
            <option value="image">图片</option>
            <option value="video">视频</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          日期
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            value={selectedDate}
            onChange={(event) => setDate(event.target.value)}
          >
            {dates.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <div className="text-xs text-muted-foreground pb-2.5">
          {selectedDate ? `${batchMarkets.length} 个市场: ${batchMarkets.join(', ')}` : '暂无反馈批次'}
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => reportsQuery.refetch()}
            disabled={!selectedDate || reportsQuery.isFetching}
          >
            <RefreshCw className={cn('h-4 w-4 mr-1.5', reportsQuery.isFetching && 'animate-spin')} />
            刷新
          </Button>
          <Button size="sm" onClick={handleGenerateClick} disabled={!selectedDate || generating}>
            {generating ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-1.5" />
            )}
            {reports.length > 0 ? '重新生成报告' : '生成报告'}
          </Button>

          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>重新生成报告？</DialogTitle>
                <DialogDescription>
                  {selectedDate} 已有 {marketGroups.length} 个市场的报告
                  {latestCreatedAt ? `（最新生成于 ${formatDateTime(latestCreatedAt)}）` : ''}
                  。重新生成将为该日期所有市场创建新版本，历史版本仍可在各报告的版本下拉中查看。
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                  取消
                </Button>
                <Button
                  onClick={() => {
                    setConfirmOpen(false)
                    generateMutation.mutate()
                  }}
                >
                  重新生成
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {reportsQuery.isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-muted-foreground">
          加载中…
        </div>
      ) : reports.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-muted-foreground">
          该日期还没有报告，点击「生成报告」按市场统计采纳/下载情况并生成 AI 分析。
        </div>
      ) : (
        <div className="space-y-4">
          <MarketSummaryTable reports={displayedReports} />
          {marketGroups.map((group, index) => {
            const report = displayedReports[index]
            return (
              <ReportCard
                key={group.market}
                report={report}
                versions={group.versions}
                newerRunning={group.versions[0].status === 'running' && report.id !== group.versions[0].id}
                onSelectVersion={(id) =>
                  setPinnedVersions((prev) => ({ ...prev, [group.market]: id }))
                }
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

function MarketSummaryTable({ reports }: { reports: FinanceReport[] }) {
  const rows = reports
    .filter((report) => report.status === 'completed')
    .map((report) => ({ market: report.market, stats: report.stats as Partial<FinanceReportStats> }))
  if (rows.length === 0) return null

  const sum = (pick: (s: Partial<FinanceReportStats>) => number | null | undefined) =>
    rows.reduce((acc, row) => acc + (pick(row.stats) ?? 0), 0)
  const totals = {
    total_variants: sum((s) => s.total_variants),
    reviewed: sum((s) => s.reviewed),
    adopted: sum((s) => s.adopted),
    rejected: sum((s) => s.rejected),
    downloads: sum((s) => s.downloads),
    downloaded_variants: sum((s) => s.downloaded_variants),
  }
  const totalAdoptionRate = totals.reviewed > 0 ? totals.adopted / totals.reviewed : null
  const totalDownloadRate =
    totals.total_variants > 0 ? totals.downloaded_variants / totals.total_variants : null

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 text-sm font-semibold">市场汇总</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-xs text-muted-foreground">
            <th className="py-1.5 pr-2 font-medium">市场</th>
            <th className="py-1.5 pr-2 font-medium">素材总数</th>
            <th className="py-1.5 pr-2 font-medium">已评审</th>
            <th className="py-1.5 pr-2 font-medium">采纳</th>
            <th className="py-1.5 pr-2 font-medium">拒绝</th>
            <th className="py-1.5 pr-2 font-medium">下载</th>
            <th className="py-1.5 pr-2 font-medium">采纳率</th>
            <th className="py-1.5 font-medium">下载率</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ market, stats }) => (
            <tr key={market} className="border-b border-gray-50">
              <td className="py-1.5 pr-2 font-medium">{market}</td>
              <td className="py-1.5 pr-2">{stats.total_variants ?? '-'}</td>
              <td className="py-1.5 pr-2">{stats.reviewed ?? '-'}</td>
              <td className="py-1.5 pr-2 text-emerald-600">{stats.adopted ?? '-'}</td>
              <td className="py-1.5 pr-2 text-red-600">{stats.rejected ?? '-'}</td>
              <td className="py-1.5 pr-2">{stats.downloads ?? '-'}</td>
              <td className="py-1.5 pr-2">{pct(stats.adoption_rate)}</td>
              <td className="py-1.5">{pct(downloadRate(stats))}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <td className="py-1.5 pr-2">合计</td>
            <td className="py-1.5 pr-2">{totals.total_variants}</td>
            <td className="py-1.5 pr-2">{totals.reviewed}</td>
            <td className="py-1.5 pr-2 text-emerald-600">{totals.adopted}</td>
            <td className="py-1.5 pr-2 text-red-600">{totals.rejected}</td>
            <td className="py-1.5 pr-2">{totals.downloads}</td>
            <td className="py-1.5 pr-2">{pct(totalAdoptionRate)}</td>
            <td className="py-1.5">{pct(totalDownloadRate)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function ReportCard({
  report,
  versions,
  newerRunning,
  onSelectVersion,
}: {
  report: FinanceReport
  versions: FinanceReport[]
  newerRunning: boolean
  onSelectVersion: (id: string) => void
}) {
  const stats = report.stats as Partial<FinanceReportStats>
  const analysis = report.analysis as Partial<FinanceReportAnalysis>
  const [showFeedback, setShowFeedback] = useState(false)

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
        <div className="text-sm font-semibold">{report.market}</div>
        <span
          className={cn(
            'rounded-full border px-2 py-0.5 text-xs',
            STATUS_STYLES[report.status] ?? 'bg-gray-50 text-gray-600 border-gray-200',
          )}
        >
          {STATUS_LABELS[report.status] ?? report.status}
        </span>
        {report.status === 'running' && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />}
        {newerRunning && (
          <span className="inline-flex items-center gap-1 text-xs text-blue-600">
            <Loader2 className="h-3 w-3 animate-spin" />
            新版本生成中…
          </span>
        )}
        {versions.length > 1 && (
          <select
            className="h-7 rounded-md border border-input bg-background px-1.5 text-xs text-foreground"
            value={report.id}
            onChange={(event) => onSelectVersion(event.target.value)}
          >
            {versions.map((version, index) => (
              <option key={version.id} value={version.id}>
                {`V${versions.length - index}${index === 0 ? '（最新）' : ''} · ${formatDateTime(version.created_at)}`}
              </option>
            ))}
          </select>
        )}
        <div className="ml-auto text-xs text-muted-foreground">
          {report.model_name ? `${report.model_name} · ` : ''}
          {formatDateTime(report.created_at)}
          {report.created_by ? ` · ${report.created_by}` : ''}
        </div>
      </div>

      {report.status === 'failed' && (
        <div className="px-4 py-3 text-sm text-red-600">生成失败：{report.error || '未知错误'}</div>
      )}

      {report.status === 'completed' && (
        <div className="space-y-4 px-4 py-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            <Stat label="素材总数" value={stats.total_variants} />
            <Stat label="已评审" value={stats.reviewed} />
            <Stat label="采纳" value={stats.adopted} highlight="text-emerald-600" />
            <Stat label="拒绝" value={stats.rejected} highlight="text-red-600" />
            <Stat label="采纳率" value={pct(stats.adoption_rate)} />
            <Stat label="下载次数" value={stats.downloads} />
            <Stat label="下载率" value={pct(downloadRate(stats))} />
          </div>

          {(stats.sections?.length ?? 0) > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-2 font-medium">组</th>
                  <th className="py-1.5 pr-2 font-medium">总数</th>
                  <th className="py-1.5 pr-2 font-medium">已评审</th>
                  <th className="py-1.5 pr-2 font-medium">采纳</th>
                  <th className="py-1.5 pr-2 font-medium">拒绝</th>
                  <th className="py-1.5 font-medium">下载</th>
                </tr>
              </thead>
              <tbody>
                {stats.sections?.map((section) => (
                  <tr key={section.variant_type} className="border-b border-gray-50">
                    <td className="py-1.5 pr-2 font-medium">{section.variant_type}</td>
                    <td className="py-1.5 pr-2">{section.total}</td>
                    <td className="py-1.5 pr-2">{section.reviewed}</td>
                    <td className="py-1.5 pr-2 text-emerald-600">{section.adopted}</td>
                    <td className="py-1.5 pr-2 text-red-600">{section.rejected}</td>
                    <td className="py-1.5">{section.downloads}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {analysis.overall_summary && (
            <AnalysisBlock title="📋 总结">
              <p className="whitespace-pre-wrap">{analysis.overall_summary}</p>
            </AnalysisBlock>
          )}
          {(analysis.adopted_patterns?.length ?? 0) > 0 && (
            <AnalysisBlock title="✅ 采纳素材的视觉共性">
              <BulletList items={analysis.adopted_patterns!} />
            </AnalysisBlock>
          )}
          {(analysis.rejected_patterns?.length ?? 0) > 0 && (
            <AnalysisBlock title="❌ 被拒素材的常见问题">
              <BulletList items={analysis.rejected_patterns!} />
            </AnalysisBlock>
          )}
          {(analysis.feedback_themes?.length ?? 0) > 0 && (
            <AnalysisBlock title="💬 反馈主题">
              <ul className="space-y-1.5">
                {analysis.feedback_themes!.map((theme, index) => (
                  <li key={index}>
                    <span className="font-medium">{theme.theme}</span>
                    {theme.evidence && (
                      <span className="text-muted-foreground">：{theme.evidence}</span>
                    )}
                  </li>
                ))}
              </ul>
            </AnalysisBlock>
          )}
          {(analysis.section_insights?.length ?? 0) > 0 && (
            <AnalysisBlock title="🔀 A/B/C 策略洞察">
              <ul className="space-y-1.5">
                {analysis.section_insights!.map((item, index) => (
                  <li key={index}>
                    <span className="font-medium">{item.variant_type} 组</span>
                    <span className="text-muted-foreground">：{item.insight}</span>
                  </li>
                ))}
              </ul>
            </AnalysisBlock>
          )}
          {(analysis.recommendations?.length ?? 0) > 0 && (
            <AnalysisBlock title="🎯 下一批建议">
              <BulletList items={analysis.recommendations!} />
            </AnalysisBlock>
          )}

          {(stats.feedback_items?.length ?? 0) > 0 && (
            <div>
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={() => setShowFeedback((prev) => !prev)}
              >
                {showFeedback ? '收起' : '展开'}反馈明细（{stats.feedback_items!.length} 条）
              </button>
              {showFeedback && (
                <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                  {stats.feedback_items!.map((item) => (
                    <li key={item.asset_id} className="rounded bg-gray-50 px-2 py-1.5">
                      [{item.variant_type ?? '?'}] {item.decision ?? '无决定'}
                      {item.score !== null ? ` · ${item.score} 分` : ''}
                      {item.downloads ? ` · 下载 ${item.downloads}` : ''}
                      {item.feedback_text ? ` · ${item.feedback_text}` : ''}
                      {item.reviewed_by ? ` — ${item.reviewed_by}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string
  value: number | string | null | undefined
  highlight?: string
}) {
  return (
    <div className="rounded-md bg-gray-50 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('text-lg font-semibold', highlight)}>{value ?? '-'}</div>
    </div>
  )
}

function AnalysisBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-gray-100 p-3 text-sm">
      <div className="mb-1.5 text-xs font-semibold text-muted-foreground">{title}</div>
      {children}
    </div>
  )
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-1 pl-5">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  )
}
